import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { skipToken } from '@reduxjs/toolkit/query';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useGetAgencyQuery } from 'entities/agency';
import { ServerErrorMessage } from 'entities/developer';
import {
  UserEditForm,
  UserInfoFields,
  useGetUserQuery,
  userZod,
  User,
  useUpdateUserMutation,
  userActions,
} from 'entities/user';
import { getRoute } from 'shared/const';
import { useAppDispatch, useAppSelector } from 'shared/hooks/hooks';
import { CardLayout } from 'shared/ui/CardLayout';
import { ResultCard } from 'shared/ui/ResultCard';
import { RectSkeleton } from 'shared/ui/Skeleton/RectSkeleton';

export const UserDetailsCard: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const userAgency = useAppSelector((state) => state.user.userAgency);
  const dispatch = useAppDispatch();

  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [isShowAdditionalData, setIsShowAdditionalData] =
    useState<boolean>(false);

  const { data: user, isFetching: isUserLoading } = useGetUserQuery(
    userId ?? skipToken
  );
  const { data: agency, isFetching: isAgencyLoading } = useGetAgencyQuery(
    user?.agencyId ?? skipToken
  );
  const [isUserUpdatingError, setIsUserUpdatingError] = useState(false);

  const [
    updateUser,
    { isLoading: isUserUpdating, isSuccess: isUserSuccessfullyUpdated },
  ] = useUpdateUserMutation();

  const form = useForm<User>({
    resolver: zodResolver(userZod),
    mode: 'onSubmit',
    defaultValues: {
      id: user?.id || undefined,
      phone: user?.phone || '',
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      position: user?.position || '',
      agencyId: user?.agencyId || null,
      isAdmin: user?.isAdmin || false,
      facebook: user?.facebook || '',
      instagram: user?.instagram || '',
      vk: user?.vk || '',
      ok: user?.ok || '',
      youtube: user?.youtube || '',
      countDay: user?.countDay || '',
      telegram: user?.telegram || '',
      whatsapp: user?.whatsapp || '',
      referral: user?.referral || '',
    },
  });

  useEffect(() => {
    if (agency) {
      dispatch(
        userActions.updateUserAgency({
          label: agency.name,
          value: agency.id,
        })
      );
    }
  }, [agency, dispatch, form, user]);

  useEffect(() => {
    if (user) {
      form.reset(user);
    }
  }, [form, user]);

  const handleUpdateUser = async (user: User) => {
    try {
      await updateUser(user).unwrap();
    } catch (error) {
      console.error(error);
      switch (error) {
        case 'ERROR_EMAIL_ALREADY_USED':
          form.setError('email', {
            message: ServerErrorMessage.ownerEmailInUse,
          });
          break;
        case 'ERROR_PHONE_ALREADY_USED':
          form.setError('phone', {
            message: ServerErrorMessage.ownerPhoneInUse,
          });
          break;
        default:
          setIsUserUpdatingError(true);
          break;
      }
    }
  };

  if (isUserSuccessfullyUpdated || isUserUpdatingError) {
    return (
      <ResultCard
        isSuccess={isUserSuccessfullyUpdated}
        successProps={{
          primaryButton: {
            navigateTo: getRoute.Users(),
          },
        }}
        errorProps={{
          primaryButton: {
            navigateTo: getRoute.Users(),
          },
        }}
      />
    );
  }

  if (isUserLoading || isAgencyLoading || !user) {
    return <RectSkeleton maxWidth={648} height={400} sx={{ m: 'auto' }} />;
  }

  return (
    <CardLayout
      header={{ title: t('user.details.card.title') }}
      footer={{
        onOkClick: isEditMode
          ? form.handleSubmit(handleUpdateUser)
          : () => setIsEditMode(true),
        onCancelClick: isEditMode
          ? () => {
              form.reset();
              setIsEditMode(false);
            }
          : () => navigate(getRoute.Users()),
        okLabel: isEditMode ? t('ui.common.save') : t('ui.common.edit'),
        cancelLabel: isEditMode ? t('ui.common.cancel') : t('ui.common.close'),
        okButtonLoading: isUserUpdating,
      }}
      maxWidth={648}
      marginAuto
      paddingY={24}
    >
      <FormProvider {...form}>
        {isEditMode ? (
          <UserEditForm
            isShowAdditionalData={isShowAdditionalData}
            onToggleAdditionalData={setIsShowAdditionalData}
            userAgency={userAgency}
          />
        ) : (
          <UserInfoFields
            isShowAdditionalData={isShowAdditionalData}
            onToggleAdditionalData={setIsShowAdditionalData}
            userAgency={userAgency}
          />
        )}
      </FormProvider>
    </CardLayout>
  );
};
